'use client';
import { useState, useEffect, useRef, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react';
import {
  Plus, Search, Upload, X, GripVertical, Play, Zap, Loader2,
  Download, Timer, HardDrive, Trash2, ImageIcon, Check,
  RotateCcw, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Clock, Sparkles, Eye,
} from 'lucide-react';
import type {
  ImageVideo, LibraryImage, ImagnImage, TextOverlay, VideoSize,
  OverlayPosition, WordBreaking, FontSize, MusicChoice, RenderProgress,
} from '@/lib/types';
import { readJson, errorMessage } from '@/lib/http';
import MusicPicker, { WORLDCUP_DEFAULT_TRACK, nameForTrackUrl } from '@/components/MusicPicker';

const NO_MUSIC: MusicChoice = { enabled: false, url: null, name: null };

// Seed the per-row music edit state from the stored columns.
const musicFromVideo = (v: ImageVideo): MusicChoice => ({
  enabled: !!v.music_enabled,
  url: v.music_url ?? null,
  name: nameForTrackUrl(v.music_url),
});

type GenStage = 'idle' | 'preparing' | 'audio' | 'rendering' | 'done' | 'error';
interface GenState {
  stage: GenStage;
  progress: number;
  videoUrl?: string;
  fileSizeBytes?: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

type PickerSource = 'imagn' | 'upload' | 'library';

// A chosen image during composition. It can be a not-yet-downloaded Imagn pick,
// a not-yet-uploaded local file, or an already-stored library image. Nothing is
// downloaded from Imagn or uploaded to S3 until the user clicks Generate.
interface ChosenImage {
  key: string;          // unique key for dedup + reorder
  previewUrl: string;   // Imagn preview URL, local object URL, or S3 url
  s3_url?: string;      // present when already stored (library pick)
  imagnId?: string;     // present for a pending Imagn download
  file?: File;          // present for a pending local upload
  caption?: string;
}

const VIDEO_SIZES: VideoSize[] = ['9:16', '16:9', '1:1', '4:5'];
const POSITIONS: OverlayPosition[] = ['top', 'center', 'bottom'];
const WORD_BREAKING: WordBreaking[] = ['one', 'few', 'several'];
const FONT_SIZES: FontSize[] = ['small', 'medium', 'big'];

// Imagn paging: fetch up to IMAGN_MAX_RESULTS across requests of IMAGN_FETCH_LIMIT,
// then show IMAGN_PER_PAGE at a time with client-side pagination.
const IMAGN_FETCH_LIMIT = 50;   // Imagn caps a single request at 50
const IMAGN_PER_PAGE = 24;      // images shown per UI page
const IMAGN_MAX_RESULTS = 300;  // safety cap on "load all"

const DEFAULT_OVERLAY: TextOverlay = {
  position: 'center',
  wordBreaking: 'one',
  size: 'big',
  color: '#FFFFFF',
};

const formatElapsed = (secs: number) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
};
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDuration = (secs: number) => {
  if (!isFinite(secs)) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};


// Upload a local image straight to S3 via a short-lived presigned PUT URL. The
// file bytes go browser → S3 directly, bypassing the app server and the nginx
// request-body limit entirely. The object is content-addressed (keyed by hash),
// so re-using the same image dedupes against what's already stored. Returns the
// public S3 URL to embed in the video.
async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function uploadFileToS3(file: File): Promise<string> {
  const contentType = file.type || 'image/jpeg';
  const hash = await sha256Hex(file);

  // 1. Ask the server for a presigned PUT (or learn it's already stored).
  const presignRes = await fetch('/api/image-library/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hash, contentType }),
  });
  if (!presignRes.ok) throw new Error(await errorMessage(presignRes, 'Could not start image upload'));
  const { uploadUrl, publicUrl, needsUpload } = (await presignRes.json()) as {
    uploadUrl?: string; publicUrl: string; needsUpload: boolean;
  };

  // 2. PUT the bytes directly to S3 (skipped when the object already exists).
  if (needsUpload && uploadUrl) {
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!put.ok) throw new Error(await errorMessage(put, 'Upload to storage failed'));
  }

  // 3. Record it in the reusable library (dedup upsert on the S3 URL).
  const reg = await fetch('/api/image-library', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ s3Url: publicUrl, caption: file.name || null }),
  });
  if (!reg.ok) throw new Error(await errorMessage(reg, 'Failed to register image'));

  return publicUrl;
}

// Aspect-ratio glyph for the video-size options (a box drawn at the true ratio).
function RatioBox({ size }: { size: VideoSize }) {
  const dims: Record<VideoSize, [number, number]> = {
    '9:16': [9, 16], '16:9': [16, 9], '1:1': [1, 1], '4:5': [4, 5],
  };
  const [w, h] = dims[size] ?? [9, 16];
  const scale = 16 / Math.max(w, h);
  return (
    <span
      className="block border-[1.5px] border-current rounded-[2px]"
      style={{ width: `${w * scale}px`, height: `${h * scale}px` }}
    />
  );
}

// Caption-position glyph: a frame with a bar at top / center / bottom.
function PositionGlyph({ position }: { position: OverlayPosition }) {
  const bar =
    position === 'top' ? 'top-[2px]'
    : position === 'center' ? 'top-1/2 -translate-y-1/2'
    : 'bottom-[2px]';
  return (
    <span className="relative block w-3.5 h-4 border-[1.5px] border-current rounded-[2px]">
      <span className={`absolute left-[2px] right-[2px] h-[2.5px] bg-current rounded-full ${bar}`} />
    </span>
  );
}

// "A" glyph sized to match the chosen font-size option.
function FontGlyph({ size }: { size: FontSize }) {
  const px = size === 'small' ? 10 : size === 'medium' ? 14 : 18;
  return <span className="font-black leading-none" style={{ fontSize: `${px}px` }}>A</span>;
}

// Section header with an optional right-aligned control.
function StepHeader({ label, right }: { label: string; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap mb-3 min-h-[28px]">
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-300">{label}</h4>
      {right}
    </div>
  );
}

// Segmented control. `icon` stacks a glyph above each label; `compact` lays the
// glyph inline with tighter padding (used in the dense details editor).
function Segmented<T extends string>({
  options, value, onChange, labels, icon, compact,
}: {
  options: T[];
  value: T;
  onChange: (v: T) => void;
  labels?: Record<string, string>;
  icon?: (opt: T) => ReactNode;
  compact?: boolean;
}) {
  const layout = compact
    ? 'flex-row items-center gap-1.5 px-2.5 py-1'
    : icon
      ? 'flex-col items-center justify-center gap-1.5 w-[3.4rem] py-2'
      : 'flex-row items-center justify-center gap-1.5 px-3 py-1.5';
  return (
    <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`flex rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border ${layout} ${
              active
                ? 'bg-white/[0.12] text-white border-white/20'
                : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/[0.04]'
            }`}
          >
            {icon && (
              <span className={`flex items-center justify-center ${compact ? '' : 'h-[18px]'} ${active ? 'text-white' : 'text-slate-500'}`}>
                {icon(opt)}
              </span>
            )}
            <span>{labels?.[opt] ?? opt}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function ImagesTab({ videos, onRefresh }: { videos: ImageVideo[]; onRefresh: () => void }) {
  const [showCreate, setShowCreate] = useState(false);

  // Creation form state.
  const [chosen, setChosen] = useState<ChosenImage[]>([]);
  const [script, setScript] = useState('');
  const [videoSize, setVideoSize] = useState<VideoSize>('9:16');
  const [overlay, setOverlay] = useState<TextOverlay>(DEFAULT_OVERLAY);
  const [music, setMusic] = useState<MusicChoice>(NO_MUSIC); // off by default for Instant Reaction

  // "Enter Player or Topic" — researches the subject with Gemini and fills the
  // Audio Script, and feeds the same subject into the Imagn image search.
  const [topic, setTopic] = useState('');
  const [reactionLoading, setReactionLoading] = useState(false);
  const [reactionError, setReactionError] = useState<string | null>(null);

  // Image picker state.
  const [source, setSource] = useState<PickerSource>('imagn');
  const [imagnQuery, setImagnQuery] = useState('');
  const [imagnResults, setImagnResults] = useState<ImagnImage[]>([]);
  const [imagnSearching, setImagnSearching] = useState(false);
  const [imagnLoadingMore, setImagnLoadingMore] = useState(false);
  const [imagnPage, setImagnPage] = useState(0); // zero-based display page
  const searchTokenRef = useRef(0); // guards against overlapping background loads
  const [library, setLibrary] = useState<LibraryImage[]>([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);

  // Full-screen video player for an existing video (the "View" overlay).
  const [viewing, setViewing] = useState<ImageVideo | null>(null);

  // Audio + generation state.
  const [previewing, setPreviewing] = useState(false);
  const [gen, setGen] = useState<GenState>({ stage: 'idle', progress: 0 });
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dragIndex = useRef<number | null>(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Close the View overlay on Escape.
  useEffect(() => {
    if (!viewing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setViewing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing]);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const start = Date.now();
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
  };
  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const loadLibrary = async () => {
    try {
      const res = await fetch('/api/image-library');
      const data = await res.json();
      setLibrary(data.images ?? []);
    } catch (e) { console.error(e); }
    finally { setLibraryLoaded(true); }
  };

  const addChosen = (item: ChosenImage) =>
    setChosen((prev) => (prev.some((c) => c.key === item.key) ? prev : [...prev, item]));
  const removeChosen = (key: string) =>
    setChosen((prev) => {
      const item = prev.find((c) => c.key === key);
      if (item?.file) URL.revokeObjectURL(item.previewUrl); // free the local object URL
      return prev.filter((c) => c.key !== key);
    });
  const isChosen = (key: string) => chosen.some((c) => c.key === key);

  // --- Image sources ---
  const fetchImagnPage = async (terms: string, offset: number): Promise<{ images: ImagnImage[]; total: number }> => {
    const res = await fetch('/api/imagn/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ terms, limit: IMAGN_FETCH_LIMIT, offset }),
    });
    const data = await res.json();
    return { images: data.images ?? [], total: data.total ?? 0 };
  };

  // `queryOverride` lets callers (e.g. the topic field) search without waiting
  // on the async imagnQuery state update. Loads the first page immediately, then
  // streams every remaining page in the background (up to the safety cap) so the
  // grid can paginate over the full result set.
  const searchImagn = async (queryOverride?: string) => {
    const q = (queryOverride ?? imagnQuery).trim();
    if (q.length < 2) return;
    const token = ++searchTokenRef.current;
    setImagnSearching(true);
    setImagnLoadingMore(false);
    setImagnResults([]);
    setImagnPage(0);
    try {
      const first = await fetchImagnPage(q, 0);
      if (searchTokenRef.current !== token) return; // superseded by a newer search
      const total = Math.min(first.total || first.images.length, IMAGN_MAX_RESULTS);
      const seen = new Set(first.images.map((i) => i.imageID));
      let all = first.images;
      setImagnResults(all);
      setImagnSearching(false);

      // Stream the rest in the background until everything is loaded (or capped).
      if (all.length < total) {
        setImagnLoadingMore(true);
        for (let offset = IMAGN_FETCH_LIMIT; offset < total && all.length < IMAGN_MAX_RESULTS; offset += IMAGN_FETCH_LIMIT) {
          const next = await fetchImagnPage(q, offset);
          if (searchTokenRef.current !== token) return; // a newer search took over
          if (!next.images.length) break;
          const fresh = next.images.filter((i) => !seen.has(i.imageID));
          fresh.forEach((i) => seen.add(i.imageID));
          if (fresh.length === 0) continue;
          all = all.concat(fresh);
          setImagnResults([...all]);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (searchTokenRef.current === token) {
        setImagnSearching(false);
        setImagnLoadingMore(false);
      }
    }
  };

  // Submit the "Enter Player or Topic" field: research the subject with Gemini
  // and drop the result into the Audio Script (without starting audio/render),
  // while feeding the same subject into the Imagn search.
  const generateReaction = async () => {
    const subject = topic.trim();
    if (!subject || reactionLoading) return;
    setReactionError(null);
    setReactionLoading(true);

    // Reuse the subject as the Imagn query and search right away.
    setSource('imagn');
    setImagnQuery(subject);
    searchImagn(subject);

    try {
      const res = await fetch('/api/reaction-script', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject }),
      });
      if (!res.ok) throw new Error(await errorMessage(res, 'Failed to generate script'));
      const { script: text } = await res.json();
      setScript(text); // fill the Audio Script — user still triggers audio/Generate manually
    } catch (e) {
      setReactionError(e instanceof Error ? e.message : 'Failed to generate script');
    } finally {
      setReactionLoading(false);
    }
  };

  // Add/remove an Imagn preview — NO download yet (avoids burning credits on
  // picks the user may discard). The real download happens at Generate time.
  const addImagn = (img: ImagnImage) => {
    const key = `imagn:${img.imageID}`;
    if (isChosen(key)) { removeChosen(key); return; }
    addChosen({
      key,
      previewUrl: img.previewUrl || img.thumbUrl,
      imagnId: img.imageID,
      caption: img.caption,
    });
  };

  // Stage local files as previews via object URLs — uploaded to S3 at Generate.
  const handleUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) =>
      addChosen({
        key: `upload:${f.name}:${f.size}:${f.lastModified}`,
        previewUrl: URL.createObjectURL(f),
        file: f,
        caption: f.name,
      }),
    );
  };

  // --- Drag reorder of chosen images ---
  const onDragStart = (i: number) => { dragIndex.current = i; };
  const onDrop = (i: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === i) return;
    setChosen((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
  };

  // --- Audio preview (same voice/settings as Signals, with timings) ---
  const previewVoice = async () => {
    if (!script.trim()) return;
    setPreviewing(true);
    try {
      const res = await fetch('/api/tts-timed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: script }),
      });
      const blob = await res.blob();
      new Audio(URL.createObjectURL(blob)).play();
    } catch (e) { console.error(e); }
    finally { setPreviewing(false); }
  };

  const resetForm = () => {
    chosen.forEach((c) => { if (c.file) URL.revokeObjectURL(c.previewUrl); });
    setChosen([]);
    setScript('');
    setTopic('');
    setReactionError(null);
    setVideoSize('9:16');
    setOverlay(DEFAULT_OVERLAY);
    setMusic(NO_MUSIC);
    setImagnResults([]);
    setImagnQuery('');
    setImagnPage(0);
    setGen({ stage: 'idle', progress: 0 });
  };

  // --- Generate video ---
  const generateVideo = async () => {
    if (chosen.length === 0 || !script.trim()) return;
    try {
      startTimer();
      setGen({ stage: 'preparing', progress: 0, startedAt: Date.now() });

      // 0. Commit the chosen previews to S3 now (and only now): download Imagn
      //    picks and upload local files, preserving order.
      const imageUrls: string[] = [];
      for (const item of chosen) {
        if (item.s3_url) {
          imageUrls.push(item.s3_url);
        } else if (item.imagnId) {
          const res = await fetch('/api/imagn/download', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageID: item.imagnId, caption: item.caption }),
          });
          if (!res.ok) throw new Error(await errorMessage(res, 'Imagn download failed'));
          imageUrls.push((await res.json()).image.s3_url);
        } else if (item.file) {
          imageUrls.push(await uploadFileToS3(item.file));
        }
      }

      // 1. Persist the video row with all options.
      setGen((p) => ({ ...p, stage: 'audio' }));
      const createRes = await fetch('/api/image-videos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          script,
          images: imageUrls,
          textOverlay: overlay,
          videoSize,
          status: 'draft',
          // Only sent when music is actually used, so the columns stay optional
          // (creation never breaks if the music migration isn't applied yet).
          ...(music.enabled || music.url ? { musicUrl: music.url, musicEnabled: music.enabled } : {}),
        }),
      });
      if (!createRes.ok) throw new Error(await errorMessage(createRes, 'Failed to save video'));
      const { video } = await createRes.json();
      const id: string = video.id;

      // 2. Generate audio + word timings (ElevenLabs, cached).
      const ttsRes = await fetch('/api/tts-timed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: script, returnUrl: true }),
      });
      if (!ttsRes.ok) throw new Error(await errorMessage(ttsRes, 'Audio generation failed'));
      const { audioUrl, wordTimingsUrl } = await ttsRes.json();

      // 3. Start the Remotion Lambda render.
      setGen((p) => ({ ...p, stage: 'rendering', progress: 0 }));
      const renderRes = await fetch('/api/render-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId: id, audioUrl, wordTimingsUrl }),
      });
      if (!renderRes.ok) throw new Error(await errorMessage(renderRes, 'Render start failed'));
      const { renderId, bucketName } = await renderRes.json();
      onRefresh();

      // 4. Poll for progress.
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(
            `/api/render-images?renderId=${renderId}&bucketName=${bucketName}&videoId=${id}`,
          );
          const progress = await readJson<RenderProgress>(pr, 'Progress check');

          if (progress.fatalErrorEncountered) {
            clearInterval(poll); stopTimer();
            setGen((p) => ({ ...p, stage: 'error', progress: 0, error: progress.errors?.[0] || 'Render failed', finishedAt: Date.now() }));
            onRefresh();
            return;
          }
          if (progress.done && progress.outputFile) {
            clearInterval(poll); stopTimer();
            setGen((p) => ({
              ...p, stage: 'done', progress: 100, videoUrl: progress.outputFile ?? undefined,
              fileSizeBytes: progress.outputSizeInBytes ?? undefined, finishedAt: Date.now(),
            }));
            onRefresh();
            return;
          }
          setGen((p) => ({
            ...p, stage: 'rendering',
            progress: Math.round((progress.overallProgress || 0) * 100),
            fileSizeBytes: progress.outputSizeInBytes ?? undefined,
          }));
        } catch (e: unknown) {
          clearInterval(poll); stopTimer();
          setGen((p) => ({ ...p, stage: 'error', progress: 0, error: e instanceof Error ? e.message : 'Progress check failed', finishedAt: Date.now() }));
        }
      }, 2000);
    } catch (e: unknown) {
      stopTimer();
      setGen((p) => ({ ...p, stage: 'error', progress: 0, error: e instanceof Error ? e.message : 'Unknown error', finishedAt: Date.now() }));
    }
  };

  const deleteVideo = async (id: string) => {
    try {
      await fetch(`/api/image-videos?id=${id}`, { method: 'DELETE' });
      onRefresh();
    } catch (e) { console.error(e); }
  };

  // --- Existing-videos list state ---
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [rowGen, setRowGen] = useState<Record<string, { stage: 'rendering' | 'error'; progress: number }>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  // Output video durations + file sizes, read on first expand (covers videos
  // rendered before file_size_bytes was persisted).
  const [durations, setDurations] = useState<Record<string, number>>({});
  const [sizes, setSizes] = useState<Record<string, number>>({});
  // Per-row edits made in the expanded detail view.
  type RowEdit = { script: string; videoSize: VideoSize; overlay: TextOverlay; music: MusicChoice };
  const [editState, setEditState] = useState<Record<string, RowEdit>>({});

  const seedEdit = (v: ImageVideo): RowEdit =>
    ({ script: v.script ?? '', videoSize: v.video_size, overlay: v.text_overlay ?? DEFAULT_OVERLAY, music: musicFromVideo(v) });
  const getEdit = (v: ImageVideo): RowEdit => editState[v.id] ?? seedEdit(v);
  const setEdit = (v: ImageVideo, patch: Partial<RowEdit>) =>
    setEditState((p) => ({ ...p, [v.id]: { ...(p[v.id] ?? seedEdit(v)), ...patch } }));

  // Force the rendered file to download instead of opening in the browser (same
  // approach as the Highlights tab): fetch as a blob and click an object URL.
  const downloadVideo = async (e: ReactMouseEvent, url: string, filename: string, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (downloading[key]) return;
    setDownloading((p) => ({ ...p, [key]: true }));
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { console.error(err); }
    finally { setDownloading((p) => ({ ...p, [key]: false })); }
  };

  const fileNameFor = (v: { id: string; script?: string | null }) => {
    const slug = (v.script ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    return `${slug || v.id}.mp4`;
  };

  // Read the output's file size from Content-Length. The bucket's CORS only
  // allows GET (not HEAD), so we issue a GET, read the header, then cancel the
  // body so the file itself isn't downloaded. Skips if already known.
  const loadSize = async (v: ImageVideo) => {
    if (!v.video_url || v.file_size_bytes || sizes[v.id] != null) return;
    try {
      const res = await fetch(v.video_url);
      const len = res.headers.get('content-length');
      res.body?.cancel().catch(() => {}); // stop the transfer after headers
      if (len) setSizes((p) => ({ ...p, [v.id]: Number(len) }));
    } catch { /* ignore */ }
  };

  // Render (or re-render) an existing row. Pass `edits` to apply detail-view
  // changes first; with no edits it simply retries the stored config.
  const runRender = async (v: ImageVideo, edits?: RowEdit) => {
    if (rowGen[v.id]?.stage === 'rendering') return;
    setRowGen((p) => ({ ...p, [v.id]: { stage: 'rendering', progress: 0 } }));
    try {
      const script = edits?.script ?? v.script ?? '';
      const videoSize = edits?.videoSize ?? v.video_size;
      const overlay = edits?.overlay ?? v.text_overlay;

      // Persist edits (incl. background music) so the render reads the updates.
      if (edits) {
        const up = await fetch('/api/image-videos', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: v.id, script, textOverlay: overlay, videoSize,
            musicUrl: edits.music.url, musicEnabled: edits.music.enabled,
          }),
        });
        if (!up.ok) throw new Error(await errorMessage(up, 'Failed to save changes'));
      }

      // Re-generate audio + timings only when the script changed (or none exists).
      let audioUrl = v.audio_url ?? undefined;
      let wordTimingsUrl = v.word_timings_url ?? undefined;
      if (!audioUrl || (edits && script !== (v.script ?? ''))) {
        const tts = await fetch('/api/tts-timed', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: script, returnUrl: true }),
        });
        if (!tts.ok) throw new Error(await errorMessage(tts, 'Audio generation failed'));
        ({ audioUrl, wordTimingsUrl } = await tts.json());
      }

      const res = await fetch('/api/render-images', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId: v.id, audioUrl, wordTimingsUrl }),
      });
      if (!res.ok) throw new Error(await errorMessage(res, 'Render start failed'));
      const { renderId, bucketName } = await res.json();
      onRefresh();
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`/api/render-images?renderId=${renderId}&bucketName=${bucketName}&videoId=${v.id}`);
          const progress = await readJson<RenderProgress>(pr, 'Progress check');
          if (progress.fatalErrorEncountered) {
            clearInterval(poll);
            setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
            onRefresh();
            return;
          }
          if (progress.done && progress.outputFile) {
            clearInterval(poll);
            setRowGen((p) => { const n = { ...p }; delete n[v.id]; return n; });
            setEditState((p) => { const n = { ...p }; delete n[v.id]; return n; });
            onRefresh();
            return;
          }
          setRowGen((p) => ({ ...p, [v.id]: { stage: 'rendering', progress: Math.round((progress.overallProgress || 0) * 100) } }));
        } catch {
          clearInterval(poll);
          setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
          onRefresh();
        }
      }, 2000);
    } catch {
      setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
    }
  };

  const isGenerating = gen.stage === 'preparing' || gen.stage === 'audio' || gen.stage === 'rendering';

  // Imagn display pagination (client-side slice of the fully-loaded results).
  const imagnTotalPages = Math.max(1, Math.ceil(imagnResults.length / IMAGN_PER_PAGE));
  const imagnPageSafe = Math.min(imagnPage, imagnTotalPages - 1);
  const imagnPageImages = imagnResults.slice(imagnPageSafe * IMAGN_PER_PAGE, imagnPageSafe * IMAGN_PER_PAGE + IMAGN_PER_PAGE);

  return (
    <div className="animate-in fade-in duration-500">
      {/* TOP BAR */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-white/10 bg-white/[0.03]">
        <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-400">
          Instant Reaction Videos
        </h3>
        <button
          onClick={() => { setShowCreate((v) => !v); if (!libraryLoaded) loadLibrary(); }}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
        >
          <Plus size={14} /> New Video
        </button>
      </div>

      {/* CREATE PANEL */}
      {showCreate && (
        <div className="px-6 md:px-8 py-6 border-b border-white/10 bg-white/[0.015] animate-in slide-in-from-top-2">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* CONTENT COLUMN — images / order / script + generate (right on desktop, below options on mobile) */}
            <div className="order-2 lg:flex-1 min-w-0 space-y-6">
              {/* INSTANT REACTION — research a player/topic, then auto-fill the
                  Audio Script and run the Imagn search for the same subject. */}
              <div>
                <StepHeader label="Enter Player or Topic" />
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-slate-900/60 border border-white/10 rounded-lg px-3 focus-within:border-white/25 transition-colors">
                    <Sparkles size={14} className="text-amber-400" />
                    <input
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && generateReaction()}
                      placeholder="e.g. Patrick Mahomes, the Cowboys' QB controversy…"
                      className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-slate-600 outline-none"
                    />
                  </div>
                  <button
                    onClick={generateReaction}
                    disabled={reactionLoading || topic.trim().length < 2}
                    className="bg-amber-500 hover:bg-amber-400 text-black px-5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
                  >
                    {reactionLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {reactionLoading ? 'Researching…' : 'Generate Script'}
                  </button>
                </div>
                <p className="mt-2 text-[10px] text-slate-600">
                  Researches the latest news and writes the Audio Script below, then searches Imagn for matching photos. Review the script, then hit Generate or Preview Voice when you&apos;re ready.
                </p>
                {reactionError && <p className="mt-1 text-[10px] text-red-400">{reactionError}</p>}
              </div>

              {/* IMAGE PICKER */}
              <div>
                <StepHeader
                  label="Choose Images"
                  right={
                    <Segmented
                      options={['imagn', 'upload', 'library'] as PickerSource[]}
                      value={source}
                      onChange={(v) => { setSource(v); if (v === 'library' && !libraryLoaded) loadLibrary(); }}
                      labels={{ imagn: 'Imagn', upload: 'Upload', library: 'Library' }}
                    />
                  }
                />

                {source === 'imagn' && (
                  <div>
                    <div className="flex gap-2 mb-3">
                      <div className="flex-1 flex items-center gap-2 bg-slate-900/60 border border-white/10 rounded-lg px-3 focus-within:border-white/25 transition-colors">
                        <Search size={14} className="text-slate-500" />
                        <input
                          value={imagnQuery}
                          onChange={(e) => setImagnQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && searchImagn()}
                          placeholder="Search Imagn (e.g. Patrick Mahomes)"
                          className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-slate-600 outline-none"
                        />
                      </div>
                      <button
                        onClick={() => searchImagn()}
                        disabled={imagnSearching || imagnQuery.trim().length < 2}
                        className="bg-white/10 hover:bg-white/20 text-white px-5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all disabled:opacity-40 flex items-center gap-2 border border-white/10"
                      >
                        {imagnSearching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        Search
                      </button>
                    </div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {imagnPageImages.map((img) => {
                        const sel = isChosen(`imagn:${img.imageID}`);
                        return (
                          <button
                            key={img.imageID}
                            onClick={() => addImagn(img)}
                            title={img.caption}
                            className={`relative aspect-square rounded-lg overflow-hidden border transition-all group ${
                              sel ? 'border-emerald-500/70 ring-2 ring-emerald-500/25' : 'border-white/10 hover:border-white/40'
                            }`}
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={img.thumbUrl} alt={img.caption} className="w-full h-full object-cover" />
                            {sel ? (
                              <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5">
                                <Check size={10} className="text-white" />
                              </div>
                            ) : (
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Plus size={18} className="text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {imagnResults.length === 0 && (
                        <div className="col-span-4 sm:col-span-6 text-center text-slate-600 text-xs py-10 font-mono uppercase tracking-widest">
                          {imagnSearching ? 'Searching…' : 'Search to find images'}
                        </div>
                      )}
                    </div>

                    {/* PAGINATION — all results are loaded, paged client-side */}
                    {imagnResults.length > 0 && (
                      <div className="flex items-center justify-between gap-3 mt-3">
                        <span className="text-[10px] font-mono text-slate-600">
                          {imagnResults.length} result{imagnResults.length === 1 ? '' : 's'}
                          {imagnLoadingMore && <span className="text-slate-500"> · loading more…</span>}
                        </span>
                        {imagnTotalPages > 1 && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setImagnPage((p) => Math.max(0, p - 1))}
                              disabled={imagnPageSafe === 0}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <ChevronLeft size={13} /> Prev
                            </button>
                            <span className="text-[10px] font-mono text-slate-400 tabular-nums min-w-[3.5rem] text-center">
                              {imagnPageSafe + 1} / {imagnTotalPages}
                            </span>
                            <button
                              onClick={() => setImagnPage((p) => Math.min(imagnTotalPages - 1, p + 1))}
                              disabled={imagnPageSafe >= imagnTotalPages - 1}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              Next <ChevronRight size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {source === 'upload' && (
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/10 rounded-xl py-12 cursor-pointer hover:border-white/30 hover:bg-white/[0.02] transition-all">
                    <input type="file" accept="image/*" multiple className="hidden"
                      onChange={(e) => { handleUpload(e.target.files); e.currentTarget.value = ''; }} />
                    <Upload size={24} className="text-slate-500" />
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                      Click to add images
                    </span>
                  </label>
                )}

                {source === 'library' && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto">
                    {library.map((img) => {
                      const sel = isChosen(img.s3_url);
                      return (
                        <button
                          key={img.id}
                          onClick={() => (sel ? removeChosen(img.s3_url) : addChosen({ key: img.s3_url, previewUrl: img.s3_url, s3_url: img.s3_url, caption: img.caption ?? undefined }))}
                          title={img.caption ?? ''}
                          className={`relative aspect-square rounded-lg overflow-hidden border transition-all ${
                            sel ? 'border-emerald-500/70 ring-2 ring-emerald-500/25' : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={img.s3_url} alt="" className={`w-full h-full object-cover transition-opacity ${sel ? 'opacity-90' : ''}`} />
                          {sel && (
                            <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5">
                              <Check size={10} className="text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                    {libraryLoaded && library.length === 0 && (
                      <div className="col-span-4 sm:col-span-6 text-center text-slate-600 text-xs py-10 font-mono uppercase tracking-widest">
                        Library is empty
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 2 · ORDER (reorderable) */}
              <div>
                <StepHeader
                  label={`Order (${chosen.length})`}
                  right={chosen.length > 0 ? <span className="text-[10px] text-slate-600 normal-case tracking-normal">drag to reorder</span> : undefined}
                />
                {chosen.length === 0 ? (
                  <div className="text-slate-600 text-xs py-6 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">
                    No images chosen yet
                  </div>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {chosen.map((img, i) => (
                      <div
                        key={`${img.key}-${i}`}
                        draggable
                        onDragStart={() => onDragStart(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => onDrop(i)}
                        className="relative shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-white/10 cursor-move group"
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={img.previewUrl} alt="" className="w-full h-full object-cover pointer-events-none" />
                        <div className="absolute top-1 left-1 bg-black/60 rounded p-0.5"><GripVertical size={12} className="text-white/70" /></div>
                        <button
                          onClick={() => removeChosen(img.key)}
                          className="absolute top-1 right-1 bg-black/60 hover:bg-red-600/90 rounded p-0.5 transition-colors"
                          title="Remove"
                        >
                          <X size={12} className="text-white" />
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black/70 rounded px-1 text-[9px] font-mono text-white">{i + 1}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 3 · SCRIPT */}
              <div>
                <StepHeader
                  label="Audio Script"
                  right={
                    <button
                      onClick={previewVoice}
                      disabled={previewing || !script.trim()}
                      className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all disabled:opacity-40"
                    >
                      <Play size={12} className={previewing ? 'animate-spin' : ''} fill="currentColor" />
                      {previewing ? 'Synthesizing…' : 'Preview Voice'}
                    </button>
                  }
                />
                <textarea
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  placeholder="Paste the audio script… e.g. Patrick Mahomes does it again…"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg p-4 text-sm text-white placeholder:text-slate-600 focus:border-white/30 outline-none h-28 resize-none transition-all"
                />
              </div>

              {/* BACKGROUND MUSIC — optional track mixed under the voiceover */}
              <MusicPicker value={music} onChange={setMusic} builtins={[WORLDCUP_DEFAULT_TRACK]} />

              {/* GENERATE — centered within the content (right) column */}
              <div className="max-w-md mx-auto w-full pt-2">
                <button
                  onClick={generateVideo}
                  disabled={isGenerating || chosen.length === 0 || !script.trim()}
                  className="w-full bg-white text-black hover:bg-white/90 py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
                  {isGenerating ? 'Generating…' : 'Generate Video'}
                </button>

                {gen.stage !== 'idle' && gen.stage !== 'done' && (
                  <div className="space-y-2 mt-3">
                    <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${gen.stage === 'error' ? 'bg-red-500' : (gen.stage === 'preparing' || gen.stage === 'audio') ? 'bg-amber-500' : 'bg-sky-500'}`}
                        style={{ width: gen.stage === 'preparing' ? '8%' : gen.stage === 'audio' ? '15%' : gen.stage === 'error' ? '100%' : `${Math.max(20, gen.progress)}%` }} />
                    </div>
                    <p className="text-[10px] font-mono text-center tracking-wide">
                      {gen.stage === 'preparing' && <span className="text-amber-400">Preparing images…</span>}
                      {gen.stage === 'audio' && <span className="text-amber-400">Generating audio…</span>}
                      {gen.stage === 'rendering' && <span className="text-sky-400">Rendering video… {gen.progress}%</span>}
                      {gen.stage === 'error' && <span className="text-red-400">Error: {gen.error}</span>}
                    </p>
                    {gen.stage !== 'error' && (
                      <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-slate-500">
                        <span className="flex items-center gap-1"><Timer size={10} />{formatElapsed(elapsed)}</span>
                        {gen.fileSizeBytes != null && gen.fileSizeBytes > 0 && (
                          <span className="flex items-center gap-1"><HardDrive size={10} />{formatFileSize(gen.fileSizeBytes)}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {gen.stage === 'done' && gen.videoUrl && (
                  <div className="space-y-2 mt-3">
                    <button
                      onClick={(e) => downloadVideo(e, gen.videoUrl!, fileNameFor({ id: 'images-script-video', script }), 'dl-gen')}
                      disabled={downloading['dl-gen']}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60">
                      {downloading['dl-gen'] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      {downloading['dl-gen'] ? 'Downloading…' : 'Download Video'}
                    </button>
                    <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-emerald-500">
                      {gen.startedAt && gen.finishedAt && (
                        <span className="flex items-center gap-1"><Timer size={10} />{formatElapsed(Math.floor((gen.finishedAt - gen.startedAt) / 1000))}</span>
                      )}
                      {gen.fileSizeBytes != null && gen.fileSizeBytes > 0 && (
                        <span className="flex items-center gap-1"><HardDrive size={10} />{formatFileSize(gen.fileSizeBytes)}</span>
                      )}
                    </div>
                    <button onClick={resetForm} className="w-full text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest py-2 transition-colors">
                      Start another
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* OPTIONS COLUMN — sized to its widest row (Video Size); left on desktop, first on mobile */}
            <div className="order-1 lg:w-max lg:shrink-0">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
                <div>
                  <span className="text-xs font-semibold text-slate-200 block mb-2">Video size</span>
                  <Segmented options={VIDEO_SIZES} value={videoSize} onChange={setVideoSize} icon={(o) => <RatioBox size={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Text position</span>
                  <Segmented options={POSITIONS} value={overlay.position} onChange={(v) => setOverlay((o) => ({ ...o, position: v }))} icon={(o) => <PositionGlyph position={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Word breaking</span>
                  <Segmented options={WORD_BREAKING} value={overlay.wordBreaking} onChange={(v) => setOverlay((o) => ({ ...o, wordBreaking: v }))} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Font size</span>
                  <Segmented options={FONT_SIZES} value={overlay.size} onChange={(v) => setOverlay((o) => ({ ...o, size: v }))} icon={(o) => <FontGlyph size={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Font color</span>
                  <div className="flex items-center gap-3">
                    <input type="color"
                      value={/^#[0-9a-fA-F]{6}$/.test(overlay.color) ? overlay.color : '#FFFFFF'}
                      onChange={(e) => setOverlay((o) => ({ ...o, color: e.target.value }))}
                      className="w-10 h-8 rounded bg-transparent border border-white/10 cursor-pointer" />
                    <input type="text"
                      value={overlay.color}
                      onChange={(e) => setOverlay((o) => ({ ...o, color: e.target.value }))}
                      spellCheck={false}
                      placeholder="#FFFFFF"
                      className="w-24 bg-slate-900/50 border border-white/10 rounded px-2 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-white/30 outline-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EXISTING VIDEOS LIST */}
      <div className="hidden md:grid grid-cols-12 px-8 py-3 text-[10px] font-black uppercase tracking-tighter text-slate-500 border-b border-white/5">
        <div className="col-span-1">Preview</div>
        <div className="col-span-3">Script</div>
        <div className="col-span-2">Size</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">Rendered</div>
        <div className="col-span-2 text-right">Action</div>
      </div>

      <div className="divide-y divide-white/5">
        {videos.length === 0 ? (
          <div className="p-16 text-center text-slate-600 font-mono text-xs uppercase tracking-widest flex flex-col items-center gap-3">
            <ImageIcon size={28} className="opacity-40" />
            No videos yet — click “New Video” to create one
          </div>
        ) : videos.map((v) => {
          const rg = rowGen[v.id];
          const isRendering = rg?.stage === 'rendering' || v.status === 'rendering';
          const failed = rg?.stage === 'error' || (v.status === 'error' && !rg);
          const expanded = expandedRow === v.id;
          const ed = getEdit(v);
          const fileSize = v.file_size_bytes ?? sizes[v.id];
          return (
          <div key={v.id} className="group">
            <div
              className="grid grid-cols-12 px-8 py-4 items-center hover:bg-white/[0.02] transition-colors cursor-pointer"
              onClick={() => { const open = !expanded; setExpandedRow(open ? v.id : null); if (open) loadSize(v); }}
            >
              <div className="col-span-1">
                <div className="w-12 h-12 rounded-lg overflow-hidden border border-white/10 bg-black">
                  {v.images?.[0] && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={v.images[0]} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
              </div>
              <div className="col-span-3 pr-4">
                <p className="text-xs text-slate-300 line-clamp-2 italic">{v.script || '— no script —'}</p>
                <p className="text-[10px] font-mono text-slate-600 mt-1">{v.images?.length ?? 0} imgs</p>
              </div>
              <div className="col-span-2 flex items-center gap-2 text-slate-400">
                <RatioBox size={v.video_size} />
                <span className="text-[11px] font-mono">{v.video_size}</span>
              </div>
              <div className="col-span-2">
                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                  v.status === 'done' ? 'bg-emerald-600/20 text-emerald-400'
                  : isRendering ? 'bg-sky-600/20 text-sky-400'
                  : failed ? 'bg-red-600/20 text-red-400'
                  : 'bg-slate-700/40 text-slate-400'
                }`}>
                  {isRendering ? (rg ? `rendering ${rg.progress}%` : 'rendering') : failed ? 'error' : v.status}
                </span>
              </div>
              <div className="col-span-2 text-[10px] font-mono text-slate-500">
                {v.status === 'done' && v.video_url
                  ? new Date(v.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </div>
              <div className="col-span-2 flex justify-end gap-1 items-center" onClick={(e) => e.stopPropagation()}>
                {failed && (
                  <button onClick={() => runRender(v)} disabled={isRendering} title="Retry render"
                    className="flex items-center gap-1 px-2 py-1.5 rounded text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors text-[10px] font-bold uppercase tracking-wider disabled:opacity-40">
                    <RotateCcw size={13} /> Retry
                  </button>
                )}
                {isRendering && <Loader2 size={14} className="animate-spin text-sky-400" />}
                {v.video_url && (
                  <button onClick={() => setViewing(v)} title="View video"
                    className="p-1.5 rounded text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 transition-colors">
                    <Eye size={14} />
                  </button>
                )}
                {v.video_url && (
                  <button onClick={(e) => downloadVideo(e, v.video_url!, fileNameFor(v), `dl-${v.id}`)}
                    disabled={downloading[`dl-${v.id}`]} title="Download video"
                    className="p-1.5 rounded text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                    {downloading[`dl-${v.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </button>
                )}
                <button onClick={() => deleteVideo(v.id)} title="Delete"
                  className="p-1.5 rounded text-slate-600 hover:text-red-500 hover:bg-red-500/10 transition-colors">
                  <Trash2 size={14} />
                </button>
                <ChevronDown size={14} className={`text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </div>
            </div>

            {expanded && (
              <div className="px-8 pb-6 pt-2 bg-black/30 border-t border-white/5 animate-in slide-in-from-top-1">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* VIDEO PREVIEW (first column) — also reads the duration once */}
                  <div className="space-y-2">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Preview</h5>
                    {v.video_url ? (
                      <video
                        src={v.video_url}
                        controls
                        preload="metadata"
                        className="w-full max-h-[440px] rounded-lg border border-white/10 bg-black object-contain"
                        onLoadedMetadata={(e) => {
                          const d = e.currentTarget.duration;
                          setDurations((p) => (p[v.id] == null ? { ...p, [v.id]: d } : p));
                        }}
                      />
                    ) : (
                      <div className="w-full aspect-[9/16] rounded-lg border border-dashed border-white/10 bg-black/40 flex items-center justify-center text-center text-slate-600 text-[10px] font-mono uppercase tracking-widest px-2">
                        {isRendering ? 'Rendering…' : 'Not rendered yet'}
                      </div>
                    )}
                  </div>

                  {/* SCRIPT (editable) + IMAGES */}
                  <div className="lg:col-span-2 space-y-4">
                    <div>
                      <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Script</h5>
                      <textarea
                        value={ed.script}
                        onChange={(e) => setEdit(v, { script: e.target.value })}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-lg p-3 text-xs text-slate-200 placeholder:text-slate-600 focus:border-white/30 outline-none h-24 resize-none"
                      />
                    </div>
                    <div>
                      <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Images ({v.images?.length ?? 0})</h5>
                      <div className="flex flex-wrap gap-2">
                        {(v.images ?? []).map((url, i) => (
                          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/10">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            <div className="absolute bottom-0.5 left-0.5 bg-black/70 rounded px-1 text-[8px] font-mono text-white">{i + 1}</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1.5">To change images, create a new video.</p>
                    </div>
                  </div>

                  {/* OPTIONS (editable) + RE-GENERATE */}
                  <div className="space-y-3">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Options</h5>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Video size</span>
                        <Segmented compact options={VIDEO_SIZES} value={ed.videoSize} onChange={(val) => setEdit(v, { videoSize: val })} icon={(o) => <RatioBox size={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Text position</span>
                        <Segmented compact options={POSITIONS} value={ed.overlay.position} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, position: val } })} icon={(o) => <PositionGlyph position={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Word breaking</span>
                        <Segmented compact options={WORD_BREAKING} value={ed.overlay.wordBreaking} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, wordBreaking: val } })} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Font size</span>
                        <Segmented compact options={FONT_SIZES} value={ed.overlay.size} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, size: val } })} icon={(o) => <FontGlyph size={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Font color</span>
                        <div className="flex items-center gap-2">
                          <input type="color"
                            value={/^#[0-9a-fA-F]{6}$/.test(ed.overlay.color) ? ed.overlay.color : '#FFFFFF'}
                            onChange={(e) => setEdit(v, { overlay: { ...ed.overlay, color: e.target.value } })}
                            className="w-8 h-7 rounded bg-transparent border border-white/10 cursor-pointer" />
                          <input type="text"
                            value={ed.overlay.color}
                            onChange={(e) => setEdit(v, { overlay: { ...ed.overlay, color: e.target.value } })}
                            spellCheck={false}
                            className="w-24 bg-slate-900/50 border border-white/10 rounded px-2 py-1 font-mono text-[11px] text-slate-200 focus:border-white/30 outline-none" />
                        </div>
                      </div>
                    </div>

                    {/* BACKGROUND MUSIC — editable, applied on re-generate */}
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                      <MusicPicker value={ed.music} onChange={(m) => setEdit(v, { music: m })} builtins={[WORLDCUP_DEFAULT_TRACK]} />
                    </div>

                    <button
                      onClick={() => runRender(v, ed)}
                      disabled={isRendering || !ed.script.trim()}
                      className="w-full mt-1 bg-white text-black hover:bg-white/90 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {isRendering ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} fill="currentColor" />}
                      {isRendering ? `Rendering ${rg?.progress ?? 0}%` : 'Re-generate Video'}
                    </button>
                    {rg?.stage === 'error' && <p className="text-[10px] text-red-400 text-center">Render failed — try again.</p>}

                    <div className="pt-1 text-[10px] text-slate-500 space-y-1">
                      {v.status === 'done' && v.video_url && (
                        <div className="flex items-center gap-4 text-slate-400">
                          {durations[v.id] != null && isFinite(durations[v.id]) ? <span className="flex items-center gap-1" title="Duration"><Clock size={10} />{formatDuration(durations[v.id])}</span> : null}
                          {fileSize ? <span className="flex items-center gap-1" title="File size"><HardDrive size={10} />{formatFileSize(fileSize)}</span> : null}
                        </div>
                      )}
                      <p>Created {new Date(v.created_at).toLocaleString()}</p>
                      {(v.audio_url || v.word_timings_url) && (
                        <div className="flex gap-3 pt-0.5">
                          {v.audio_url && <a href={v.audio_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1"><ExternalLink size={10} /> Audio</a>}
                          {v.word_timings_url && <a href={v.word_timings_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1"><ExternalLink size={10} /> Timings</a>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* VIEW OVERLAY — play an existing video, with a download action */}
      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in"
          onClick={() => setViewing(null)}
        >
          <div
            className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10">
              <p className="text-xs text-slate-300 italic line-clamp-1">{viewing.script || '— no script —'}</p>
              <button
                onClick={() => setViewing(null)}
                title="Close"
                className="shrink-0 p-1.5 rounded text-slate-500 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 min-h-0 bg-black flex items-center justify-center">
              {viewing.video_url ? (
                <video src={viewing.video_url} controls autoPlay className="max-h-[70vh] max-w-full" />
              ) : (
                <p className="text-slate-600 text-xs font-mono uppercase tracking-widest py-20">
                  No video available
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10">
              {viewing.video_url && (
                <button
                  onClick={(e) => downloadVideo(e, viewing.video_url!, fileNameFor(viewing), `dl-modal-${viewing.id}`)}
                  disabled={downloading[`dl-modal-${viewing.id}`]}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-60"
                >
                  {downloading[`dl-modal-${viewing.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {downloading[`dl-modal-${viewing.id}`] ? 'Downloading…' : 'Download'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
